import { PageLoader } from '@/components/ui/loader';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, CreditCard, Loader2, Users } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermission } from '@/hooks/use-permission';
import { ApiRequestError, apiGet, apiPost } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';

type Billing = {
  name: string;
  plan: string;
  subscriptionStatus: string;
  trialEndsAt: string | null;
  seatLimit: number | null;
  currentPeriodEnd: string | null;
  billingInterval: string | null;
  seatsUsed: number;
  vatPercent: number;
  onlinePayment: boolean;
  plans: { key: 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE'; name: string; monthly: string }[];
  history: { id: string; plan: string | null; seats: number | null; interval: string | null; amount: string; currency: string; status: string; paidAt: string | null; createdAt: string; provider: string }[];
};

const FEATURES: Record<string, string[]> = {
  STARTER: ['Leads, contacts & deals', 'Pipelines & inbox', 'Import & export'],
  PROFESSIONAL: ['Everything in Starter', 'Salesbots, sequences, booking pages', 'Forms, landing pages, quotes & invoices'],
  ENTERPRISE: ['Everything in Professional', 'Help desk, SLAs & help centre', 'Custom roles, audit log, onboarding'],
};

const STATUS: Record<string, [string, string]> = {
  TRIALING: ['Free trial', 'bg-blue-100 text-blue-700'],
  ACTIVE: ['Active', 'bg-emerald-100 text-emerald-700'],
  PAST_DUE: ['Payment due', 'bg-amber-100 text-amber-700'],
  SUSPENDED: ['Suspended', 'bg-red-100 text-red-700'],
  CANCELLED: ['Cancelled', 'bg-neutral-200 text-neutral-600'],
};

/** The workspace paying for DovoCRM: plan, seats, period, receipts. */
export function BillingPage() {
  const { can } = usePermission();
  const { data, isLoading } = useQuery({ queryKey: ['billing'], queryFn: () => apiGet<Billing>('/billing') });
  const [plan, setPlan] = useState<'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE'>('PROFESSIONAL');
  const [seats, setSeats] = useState(1);
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('yearly');

  useEffect(() => {
    if (!data) return;
    if (['STARTER', 'PROFESSIONAL', 'ENTERPRISE'].includes(data.plan)) setPlan(data.plan as typeof plan);
    setSeats(Math.max(data.seatsUsed, data.seatLimit ?? 1));
    if (data.billingInterval === 'monthly' || data.billingInterval === 'yearly') setInterval(data.billingInterval);
  }, [data]);

  const { data: price } = useQuery({
    queryKey: ['billing', 'quote', plan, seats, interval],
    queryFn: () => apiPost<{ net: string; vat: string; total: string; vatPercent: number }>('/billing/quote', { plan, seats, interval }),
    enabled: seats >= 1,
  });
  const checkout = useMutation({
    mutationFn: () => apiPost<{ url: string }>('/billing/checkout', { plan, seats, interval }),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (failure) => toast.error(failure instanceof ApiRequestError ? failure.message : 'Could not open the payment page'),
  });

  if (isLoading || !data) return <PageLoader />;
  const [statusLabel, statusClass] = STATUS[data.subscriptionStatus] ?? [data.subscriptionStatus, ''];
  const daysLeft = data.subscriptionStatus === 'TRIALING' && data.trialEndsAt ? Math.ceil((new Date(data.trialEndsAt).getTime() - Date.now()) / 86_400_000) : null;

  return (
    <div>
      <PageHeader title="Plan & billing" description="Your DovoCRM subscription. Prices are in dirhams, per user, plus VAT." breadcrumbs={[{ label: 'Administration' }, { label: 'Plan & billing' }]} />

      <div className="mb-6 grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Plan</p>
            <p className="mt-1 flex items-center gap-2 text-2xl font-extrabold">
              {data.plan === 'TRIAL' ? 'Trial' : data.plan.charAt(0) + data.plan.slice(1).toLowerCase()}
              <Badge className={statusClass}>{statusLabel}</Badge>
            </p>
            {daysLeft !== null && <p className="text-sm text-muted-foreground">{daysLeft > 0 ? `${daysLeft} days left` : 'Trial has ended'}</p>}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Users</p>
            <p className="mt-1 flex items-center gap-2 text-2xl font-extrabold">
              <Users className="h-5 w-5 text-muted-foreground" /> {data.seatsUsed}
              {data.seatLimit ? <span className="text-base font-medium text-muted-foreground">of {data.seatLimit} seats</span> : null}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Paid until</p>
            <p className="mt-1 text-2xl font-extrabold">{data.currentPeriodEnd ? formatDate(data.currentPeriodEnd) : '—'}</p>
            {data.billingInterval && <p className="text-sm capitalize text-muted-foreground">{data.billingInterval}</p>}
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{data.subscriptionStatus === 'ACTIVE' ? 'Renew or change your plan' : 'Choose your plan'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-3 md:grid-cols-3">
            {data.plans.map((item) => (
              <button
                key={item.key}
                onClick={() => setPlan(item.key)}
                className={cn('rounded-2xl border-2 p-4 text-start transition-colors', plan === item.key ? 'border-primary bg-primary/5' : 'hover:border-primary/40')}
              >
                <p className="font-bold">{item.name}</p>
                <p className="mt-1">
                  <span className="text-2xl font-extrabold">AED {Number(item.monthly).toFixed(0)}</span>
                  <span className="text-sm text-muted-foreground"> / user / month</span>
                </p>
                <ul className="mt-3 space-y-1">
                  {FEATURES[item.key].map((feature) => (
                    <li key={feature} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Check className="h-3 w-3 text-primary" /> {feature}
                    </li>
                  ))}
                </ul>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">Users</p>
              <Input type="number" min={data.seatsUsed} max={500} className="w-28" value={seats} onChange={(event) => setSeats(Math.max(1, Number(event.target.value) || 1))} />
            </div>
            <div className="flex rounded-xl border p-1">
              {(['monthly', 'yearly'] as const).map((value) => (
                <button key={value} onClick={() => setInterval(value)} className={cn('rounded-lg px-4 py-1.5 text-sm', interval === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}>
                  {value === 'monthly' ? 'Monthly' : 'Yearly — 2 months free'}
                </button>
              ))}
            </div>
          </div>
          {seats < data.seatsUsed && <p className="text-sm text-destructive">You have {data.seatsUsed} users — choose at least that many seats.</p>}
          {price && (
            <div className="max-w-sm space-y-1 rounded-xl bg-muted/40 p-4 text-sm">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>AED {price.net}</span>
              </div>
              <div className="flex justify-between">
                <span>VAT {price.vatPercent}%</span>
                <span>AED {price.vat}</span>
              </div>
              <div className="flex justify-between border-t pt-1 text-base font-bold">
                <span>Total</span>
                <span>AED {price.total}</span>
              </div>
            </div>
          )}
          {data.onlinePayment ? (
            can('admin.organization.update') && (
              <Button size="lg" onClick={() => checkout.mutate()} disabled={checkout.isPending || seats < data.seatsUsed}>
                {checkout.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay by card
              </Button>
            )
          ) : (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Card payment is being set up. To subscribe now, email <a className="underline" href="mailto:hello@dovocrm.com">hello@dovocrm.com</a>.
            </p>
          )}
        </CardContent>
      </Card>

      {data.history.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Payments</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.history.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{formatDate(row.paidAt ?? row.createdAt)}</TableCell>
                    <TableCell className="text-sm">
                      {row.plan} · {row.seats} users · {row.interval}
                    </TableCell>
                    <TableCell>
                      {row.currency} {row.amount}
                    </TableCell>
                    <TableCell>
                      <Badge className={row.status === 'PAID' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{row.status === 'PAID' ? 'Paid' : 'Failed'}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
